import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useProvisionToken } from '../hooks/useProvisionToken';

interface ApiUrlGateProps {
  children: ReactNode;
}

export function ApiUrlGate({ children }: ApiUrlGateProps) {
  const envUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
  const storedUrl = localStorage.getItem('clawer_api_url');
  const params = new URLSearchParams(window.location.search);
  const provisionToken = params.get('provision');

  // If we have a provision token, handle it before anything else
  if (provisionToken) {
    return <ProvisionHandler token={provisionToken} />;
  }

  // Skip gate if a URL is already configured
  if (envUrl || storedUrl) {
    return <>{children}</>;
  }

  return <ApiUrlForm />;
}

function ProvisionHandler({ token }: { token: string }) {
  const { status, error, consumeToken } = useProvisionToken();

  useEffect(() => {
    void consumeToken(token);
  }, [token, consumeToken]);

  useEffect(() => {
    if (status === 'success') {
      // Strip the provision param and reload
      const url = new URL(window.location.href);
      url.searchParams.delete('provision');
      window.location.replace(url.toString());
    }
  }, [status]);

  if (status === 'error') {
    return (
      <div className="min-h-[100dvh] bg-canvas flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-error-bg mb-4">
            <span className="material-icons-round text-error text-xl">error</span>
          </div>
          <h1 className="text-2xl font-bold text-ink tracking-tight">Provisioning Failed</h1>
          <p className="text-sm text-ink-secondary mt-2">{error}</p>
          <p className="text-xs text-ink-tertiary mt-4">
            Contact your administrator or try the link again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-canvas flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <span className="material-icons-round text-accent text-4xl animate-spin">sync</span>
        <p className="text-sm text-ink-secondary">Setting up your instance...</p>
      </div>
    </div>
  );
}

function ApiUrlForm() {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = url.replace(/\/+$/, '').trim();
    if (!trimmed) return;

    setConnecting(true);
    setError('');

    try {
      const resp = await fetch(`${trimmed}/health`, {
        signal: AbortSignal.timeout(5000),
      });
      const body = await resp.json() as { status?: string };
      if (!body.status) throw new Error('Not a Clawer server');
      localStorage.setItem('clawer_api_url', trimmed);
      window.location.reload();
    } catch {
      setError('Could not connect to server. Check the URL and try again.');
      setConnecting(false);
    }
  }

  return (
    <div className="min-h-[100dvh] bg-canvas flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-ink mb-4">
            <span className="material-icons-round text-white text-xl">sensors</span>
          </div>
          <h1 className="text-2xl font-bold text-ink tracking-tight">Connect to Clawer</h1>
          <p className="text-sm text-ink-secondary mt-1.5">
            Enter your backend server URL to get started
          </p>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label htmlFor="api-url" className="block text-xs font-medium text-ink-secondary mb-1.5">
              Server URL
            </label>
            <input
              id="api-url"
              type="url"
              value={url}
              onChange={(e) => { setUrl(e.target.value); setError(''); }}
              placeholder="https://your-server.example.com"
              autoFocus
              required
              disabled={connecting}
              className="w-full px-3 py-2.5 rounded-lg border border-edge bg-white text-sm text-ink placeholder:text-ink-tertiary focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent disabled:opacity-60"
            />
            {error && (
              <p className="text-xs text-error mt-1.5 flex items-start gap-1">
                <span className="material-icons-round text-xs mt-px shrink-0">error</span>
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={connecting || !url.trim()}
            className="w-full px-4 py-2.5 rounded-lg bg-ink text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center justify-center gap-2"
          >
            {connecting ? (
              <>
                <span className="material-icons-round text-sm animate-spin">sync</span>
                Connecting...
              </>
            ) : (
              'Connect'
            )}
          </button>
        </form>

        <p className="text-xs text-ink-tertiary text-center mt-6">
          You can change this later in Settings.
        </p>
      </div>
    </div>
  );
}
